import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions, ScriptableContext } from "chart.js";
import { useChartTheme } from "@/lib/chartTheme";
import { registerCharts } from "./register";
import { withAlpha, mergeOptions } from "./utils";

registerCharts();

export interface LineSeries {
  label: string;
  data: number[];
  /** Optional override colour; otherwise falls through to the theme palette. */
  color?: string;
  /** A "focal" series gets the accent colour + a gradient fill (max one). */
  focal?: boolean;
  /** Which y-axis this series is plotted against. Defaults to "left".
   *  Use "right" to overlay a series on a second, independently-scaled axis
   *  (e.g. an amount alongside counts). */
  axis?: "left" | "right";
}

interface LineChartProps {
  labels: string[];
  series: LineSeries[];
  showLegend?: boolean;
  /** Tick/tooltip formatter for the left axis. */
  formatY?: (value: number) => string;
  /** Tick/tooltip formatter for the right axis (only used when a series
   *  sets `axis: "right"`). */
  formatRightY?: (value: number) => string;
  height?: number;
  className?: string;
  /** Delay (ms) before the entry animation starts. Use to coordinate
   *  with parent fade-in so the user actually sees the chart animate. */
  delay?: number;
}

/** Vertical gradient — colour-with-alpha at top, transparent at bottom. */
function gradientFill(colour: string, topAlpha: number) {
  return (ctx: ScriptableContext<"line">) => {
    const { chart } = ctx;
    const { ctx: cv, chartArea } = chart;
    if (!chartArea) return undefined;
    const g = cv.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    g.addColorStop(0, withAlpha(colour, topAlpha));
    g.addColorStop(1, withAlpha(colour, 0));
    return g;
  };
}

export function LineChart({
  labels,
  series,
  showLegend = true,
  formatY,
  formatRightY,
  height = 280,
  className,
  delay = 0,
}: LineChartProps) {
  const { tokens, options: base, palette } = useChartTheme();

  const hasRightAxis = series.some((s) => s.axis === "right");

  const data = useMemo<ChartData<"line">>(() => {
    return {
      labels,
      datasets: series.map((s, i) => {
        const colour = s.color ?? (s.focal ? tokens.accent : palette[(i + 1) % palette.length]);
        return {
          label: s.label,
          data: s.data,
          yAxisID: s.axis === "right" ? "y1" : "y",
          borderColor: colour,
          backgroundColor: s.focal
            ? gradientFill(colour, 0.22)
            : withAlpha(colour, 0.06),
          borderWidth: s.focal ? 2 : 1.5,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: tokens.surface,
          pointHoverBorderColor: colour,
          fill: s.focal,
          // Non-focal series get a dashed stroke for clear visual hierarchy.
          borderDash: s.focal ? undefined : [4, 4],
        };
      }),
    };
  }, [labels, series, tokens, palette]);

  const options = useMemo<ChartOptions<"line">>(() => {
    // Only include `scales` in the override when there's something to override.
    // `mergeOptions` deep-merges objects but assigns a leaf `undefined`, which
    // would clobber the base's `scales.y` config and trip Chart.js's scale
    // validation ("Invalid scale configuration for scale: y").
    const overrides: ChartOptions<"line"> = {
      plugins: { legend: { display: showLegend } },
      animation: {
        duration: 900,
        easing: "easeOutQuart",
        delay,
      },
    };

    // With two axes, format each series' tooltip value with the formatter for
    // the axis it sits on, so counts and amounts read correctly together. Left
    // for single-axis charts so their existing tooltips are unchanged.
    if (hasRightAxis) {
      overrides.plugins!.tooltip = {
        callbacks: {
          label: (ctx) => {
            const fmt = ctx.dataset.yAxisID === "y1" ? formatRightY : formatY;
            const text = fmt ? fmt(Number(ctx.parsed.y)) : ctx.formattedValue;
            return ` ${ctx.dataset.label}: ${text}`;
          },
        },
      };
    }

    const scales: NonNullable<ChartOptions<"line">["scales"]> = {};
    if (formatY) {
      scales.y = { ticks: { callback: (v) => formatY(Number(v)) } };
    }
    if (hasRightAxis) {
      // A second axis on the right, independently scaled. Its gridlines are
      // suppressed (the left axis already draws them) so the plot stays clean.
      scales.y1 = {
        position: "right",
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        border: { display: false },
        ticks: {
          color: tokens.fgSubtle,
          font: { family: tokens.fontMono, size: 11 },
          padding: 8,
          maxTicksLimit: 5,
          callback: formatRightY ? (v) => formatRightY(Number(v)) : undefined,
        },
      };
    }
    if (Object.keys(scales).length > 0) {
      overrides.scales = scales;
    }

    return mergeOptions(base as ChartOptions<"line">, overrides);
  }, [base, showLegend, formatY, formatRightY, hasRightAxis, tokens, delay]);

  return (
    <div className={className} style={{ height }}>
      <Line data={data} options={options} />
    </div>
  );
}
